import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { parseApiResponse, normalizeAppError } from '../utils/apiErrors';
import { API, API_BASE } from '../config/apiBase';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const getToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');
    const removeToken = () => { localStorage.removeItem('token'); sessionStorage.removeItem('token'); };
    const saveToken = (token, remember) => {
        if (remember) { localStorage.setItem('token', token); } 
        else { sessionStorage.setItem('token', token); }
    };

    const checkAuth = useCallback(async () => {
        try {
            const token = getToken();
            if (!token) {
                setUser(null);
                setLoading(false);
                return;
            }
            
            const response = await fetch(`${API}/auth/me`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const text = await response.text();
                try {
                    const userData = JSON.parse(text);
                    setUser(userData);
                } catch (_) {
                    setUser(null);
                    removeToken();
                }
            } else {
                setUser(null);
                removeToken();
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        if (window.location.pathname === '/auth/callback') {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password, remember = true) => {
        const response = await fetch(`${API}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email, password })
        });

        const { data, error } = await parseApiResponse(response, 'login');
        if (error) throw new Error(error.message);

        setUser(data.user);
        saveToken(data.token, remember);
        return data;
    };

    const register = async (name, email, password, remember = true) => {
        const response = await fetch(`${API}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ name, email, password })
        });

        const { data, error } = await parseApiResponse(response, 'register');
        if (error) throw new Error(error.message);

        setUser(data.user);
        saveToken(data.token, remember);
        return data;
    };

    const loginWithGoogle = () => {
        const redirectUrl = window.location.origin + '/auth/callback';
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        if (!clientId) { console.error('Google Client ID not configured'); return; }
        const params = new URLSearchParams({
            client_id: clientId,
            redirect_uri: redirectUrl,
            response_type: 'code',
            scope: 'email profile',
            access_type: 'offline',
            prompt: 'select_account',
        });
        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    };

    const loginWithFacebook = () => {
        const redirectUrl = window.location.origin + '/auth/callback';
        const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
        if (!appId) { console.error('Facebook App ID not configured'); return; }
        const params = new URLSearchParams({
            client_id: appId,
            redirect_uri: redirectUrl,
            response_type: 'code',
            scope: 'email,public_profile',
            state: 'facebook',
        });
        window.location.href = `https://www.facebook.com/v21.0/dialog/oauth?${params.toString()}`;
    };

    const logout = async () => {
        try {
            await fetch(`${API}/auth/logout`, {
                method: 'POST',
                credentials: 'include'
            });
        } catch (error) {
            console.error('Logout error:', error);
        }
        
        setUser(null);
        removeToken();
        // Clear all app-related storage for total logout
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k && k !== 'sv_theme' && k !== 'sv_layout') keysToRemove.push(k);
        }
        keysToRemove.forEach(k => localStorage.removeItem(k));
        sessionStorage.clear();
    };

    const value = {
        user,
        loading,
        login,
        register,
        loginWithGoogle,
        loginWithFacebook,
        logout,
        checkAuth,
        setUser,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isApproved: user?.role === 'approved' || user?.role === 'admin',
        canAccessSystem: ['admin', 'approved', 'empresa', 'empresa_gestor', 'colaborador'].includes(user?.role) || ['client', 'empresa'].includes(user?.account_type),
        isEmpresa: user?.account_type === 'empresa' || user?.account_type === 'client',
        isColaborador: user?.account_type === 'colaborador' || user?.account_type === 'client_user',
        isPlatform: !user?.account_type || user?.account_type === 'platform',
        // Legacy aliases
        isClient: user?.account_type === 'empresa' || user?.account_type === 'client',
        isClientUser: user?.account_type === 'colaborador' || user?.account_type === 'client_user',
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
